import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { formatDateTime } from "../utils/datetime";

/**
 * The outcome of the last credentialed agent deploy.
 *
 * WHY THIS COMPONENT EXISTS. The re-deploy button queued the work and said
 * "queued for 38 device(s)" — and that was the last the technician heard.
 * Every per-machine result was written to the database and rendered by
 * nothing. Measured over 30 days before this: 535 attempts, 98 successes,
 * and the same machines re-attempted up to ten times each, because there was
 * no screen on which to learn they were simply switched off.
 *
 * GROUPED BY CAUSE, NOT LISTED BY MACHINE. The first cut of this panel
 * printed the full explanation once per machine; on a real 14-machine run
 * that meant the same two paragraphs fourteen times, with the raw protocol
 * output swamping the sentence it was supposed to support. A deploy where
 * everything failed the same way has ONE finding and a list of names, and
 * saying it once is what makes it readable.
 */

type Diagnosis = {
  code: string;
  ok: boolean;
  title: string;
  means: string;
  fix: string;
  side: "target" | "credential" | "ours" | "unknown";
  retry_worthwhile: boolean;
};

type Row = { target: string; raw: string; diagnosis: Diagnosis | null; ok: boolean };

type Result = {
  found: boolean;
  command_id?: number;
  status?: string;
  completed_at?: string | null;
  deployer_hostname?: string | null;
  total?: number;
  succeeded?: number;
  failed?: number;
  targets?: Row[];
};

const SIDE: Record<string, { label: string; chip: string; ring: string }> = {
  target: {
    label: "The machine",
    chip: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
    ring: "border-sky-200 dark:border-sky-800/60 bg-sky-50/50 dark:bg-sky-900/10",
  },
  credential: {
    label: "Deploy account",
    chip: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    ring: "border-amber-200 dark:border-amber-800/60 bg-amber-50/50 dark:bg-amber-900/10",
  },
  ours: {
    label: "Our end",
    chip: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200",
    ring: "border-purple-200 dark:border-purple-800/60 bg-purple-50/50 dark:bg-purple-900/10",
  },
  unknown: {
    label: "Unclear",
    chip: "bg-slate-200 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200",
    ring: "border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/30",
  },
};

type Group = { key: string; diagnosis: Diagnosis | null; rows: Row[] };

export default function DeployResultPanel({ customerId }: { customerId: number }) {
  const [res, setRes] = useState<Result | null>(null);
  const [open, setOpen] = useState(false);
  const [rawFor, setRawFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/api/v1/network-discovery/deploy-result", {
        params: { customer_id: customerId },
      });
      setRes(r.data);
    } catch {
      // No result is not an error worth shouting about; the panel just
      // doesn't render.
      setRes(null);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!res?.found || !res.total) return null;

  const failures = (res.targets || []).filter((r) => !r.ok);
  const groups: Group[] = [];
  for (const r of failures) {
    const key = r.diagnosis?.code || "unexplained";
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, diagnosis: r.diagnosis, rows: [] };
      groups.push(g);
    }
    g.rows.push(r);
  }
  groups.sort((a, b) => b.rows.length - a.rows.length);

  return (
    <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left"
      >
        <span className="text-xs text-gray-700 dark:text-gray-200">
          <span className="font-semibold">Last deploy</span>
          {res.completed_at ? ` · ${formatDateTime(res.completed_at)}` : " · in progress"}
          {res.deployer_hostname ? ` · from ${res.deployer_hostname}` : ""}
          <span className="ml-2 text-emerald-700 dark:text-emerald-400 font-medium">
            {res.succeeded} installed
          </span>
          {res.failed ? (
            <span className="ml-2 text-red-700 dark:text-red-400 font-medium">
              {res.failed} failed
            </span>
          ) : null}
        </span>
        <span className="text-xs text-gray-500 shrink-0">{open ? "Hide" : "Show detail"}</span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          {groups.map((g) => {
            const d = g.diagnosis;
            const s = SIDE[d?.side || "unknown"];
            return (
              <div key={g.key} className={`rounded-md border p-2.5 ${s.ring}`}>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${s.chip}`}>
                    {s.label}
                  </span>
                  <span className="text-[11.5px] font-semibold text-gray-900 dark:text-gray-100">
                    {d ? d.title : "Result not recognised"}
                  </span>
                  <span className="text-[10.5px] text-gray-500 dark:text-gray-400">
                    · {g.rows.length} machine{g.rows.length === 1 ? "" : "s"}
                  </span>
                </div>

                {d && (
                  <div className="mt-1.5 grid gap-1.5 sm:grid-cols-2">
                    <div className="text-[10.5px] text-gray-600 dark:text-gray-400 leading-snug">
                      {d.means}
                    </div>
                    <div className="text-[10.5px] text-gray-700 dark:text-gray-300 leading-snug">
                      <span className="font-semibold">What to do: </span>
                      {d.fix}
                    </div>
                  </div>
                )}

                <div className="mt-2 flex flex-wrap gap-1">
                  {g.rows.map((r) => (
                    <button
                      key={r.target}
                      onClick={() => setRawFor(rawFor === r.target ? null : r.target)}
                      title="Show the raw output for this machine"
                      className="font-mono text-[10px] px-1.5 py-0.5 rounded border border-gray-300
                        dark:border-gray-600 bg-white/70 dark:bg-gray-900/50 text-gray-700
                        dark:text-gray-300 hover:border-gray-500"
                    >
                      {r.target}
                    </button>
                  ))}
                </div>

                {/* The raw line is always reachable, one click away, per machine.
                    It is not printed inline because on a run where every machine
                    failed identically it buried the one sentence that mattered —
                    but it must never be unreachable, because an unrecognised
                    result has nothing else. */}
                {g.rows.some((r) => r.target === rawFor) && (
                  <div className="mt-1.5 font-mono text-[10px] text-gray-600 dark:text-gray-400 break-words bg-white/60 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded p-1.5">
                    <span className="font-sans font-semibold text-gray-500">{rawFor}: </span>
                    {g.rows.find((r) => r.target === rawFor)?.raw || "(no output recorded)"}
                  </div>
                )}
              </div>
            );
          })}

          {res.succeeded ? (
            <div className="text-[10.5px] text-emerald-800 dark:text-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/60 rounded-md px-2.5 py-1.5">
              {res.succeeded} machine{res.succeeded === 1 ? "" : "s"} installed successfully.
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
